import { createContext, useContext, useState, ReactNode } from 'react';

interface LiquiditySettings {
  feeCollectorAddress: string;
  addLiquidityFee: number;
  removeLiquidityFee: number;
}

interface LiquiditySettingsContextType {
  settings: LiquiditySettings;
  updateSettings: (newSettings: Partial<LiquiditySettings>) => void;
}

const DEFAULT_LIQUIDITY_SETTINGS: LiquiditySettings = {
  feeCollectorAddress: "0x31dB0dBa8d2D0d2a58D3286E1D3a1aF778c832A0",
  addLiquidityFee: 0.1, // 0.1%
  removeLiquidityFee: 0.2, // 0.2%
};

const LiquiditySettingsContext = createContext<LiquiditySettingsContextType | undefined>(undefined);

export function LiquiditySettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<LiquiditySettings>(() => {
    try {
      const savedSettings = localStorage.getItem('liquiditySettings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        // Validate the structure of saved settings
        if (parsedSettings.feeCollectorAddress && 
            typeof parsedSettings.addLiquidityFee === 'number' && 
            typeof parsedSettings.removeLiquidityFee === 'number') {
          return parsedSettings;
        }
      }
    } catch (error) {
      console.error('Error loading liquidity settings:', error);
    }
    return DEFAULT_LIQUIDITY_SETTINGS;
  });

  const updateSettings = (newSettings: Partial<LiquiditySettings>) => {
    setSettings(prevSettings => {
      const updatedSettings = { 
        ...prevSettings,
        ...newSettings,
      };
      try {
        localStorage.setItem('liquiditySettings', JSON.stringify(updatedSettings));
      } catch (error) {
        console.error('Error saving liquidity settings:', error);
      }
      return updatedSettings;
    });
  };

  return (
    <LiquiditySettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </LiquiditySettingsContext.Provider>
  );
}

export function useLiquiditySettings() {
  const context = useContext(LiquiditySettingsContext);
  if (context === undefined) {
    throw new Error('useLiquiditySettings must be used within a LiquiditySettingsProvider');
  }
  return context;
}
